import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from "@angular/router";
import { BehaviorSubject } from 'rxjs';
import { GlobalConstants } from '../Constants/global.constants';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private permissionsMap: Record<string, string[]> = {};
  private permissionsSubject = new BehaviorSubject<any[]>([]);

  // Public observable for components to subscribe to
  public permissions$ = this.permissionsSubject.asObservable();

  constructor(private router: Router) {
    this.loadPermissions();

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.loadPermissions();
      }
    });
  }

  //Process and save permissions (to be called from login)
  processAndSavePermissions(permissions: any[]): void {
    // Save to localStorage
    localStorage.setItem('permissions', JSON.stringify(permissions));

    // Reload permissions
    this.loadPermissions();
  }

  loadPermissions() {
    const storedPermissions = localStorage.getItem('permissions');
    const permissions = storedPermissions ? JSON.parse(storedPermissions) : [];

    // Normalize paths and sort
    const processedPermissions = permissions.map((perm: any) => ({
      ...perm,
      RoutePath: this.normalizePath(perm.RoutePath),
    })).sort((a: any, b: any) => a.SortOrder - b.SortOrder);

    // Update permissions map for route-based checks
    this.permissionsMap = {};
    processedPermissions.forEach((perm: any) => {
      const route = this.normalizePath(perm.RoutePath);
      this.permissionsMap[route] = perm.Functions.map((f: string) => f.toLowerCase());
    });

    // Notify subscribers about updated permissions
    this.permissionsSubject.next(processedPermissions);
  }

  // Get processed permissions for menu filtering
  getProcessedPermissions(): any[] {
  const storedPermissions = localStorage.getItem('permissions');
  const permissions = storedPermissions ? JSON.parse(storedPermissions) : [];

  return permissions.map((perm: any) => {
    let routePath = this.normalizePath(perm.RoutePath);
    if (routePath === '/usermanagement') {
      routePath = GlobalConstants.CONTROL_HUB_URL + routePath;
    }
    return {
      ...perm,
      RoutePath: routePath,
    };
  }).sort((a: any, b: any) => a.SortOrder - b.SortOrder);
}

  // Get allowed routes for menu filtering
  getAllowedRoutesForMenu(): any[] {
    const processedPermissions = this.getProcessedPermissions();
    return processedPermissions.map((p: any) => ({
      route: p.RoutePath,
      sortOrder: p.SortOrder
    }));
  }

  normalizePath(path: string): string {
    if (!path) return "";
    path = path.trim().replace(/\/+/g, "/");
    if (!path.startsWith("/")) path = "/" + path;
    return path.toLowerCase();
  }

  can(action: string, route?: string): boolean {
    const currentRoute = route
      ? this.normalizePath(route)
      : this.normalizePath(this.router.url.split("?")[0]);

    const allowedActions = this.permissionsMap[currentRoute] || [];
    return allowedActions.includes(action.toLowerCase());
  }

}
